import { IMediaSupport, mediaSupport } from "../../common/media-renderer/media-support";
import { isVisualFileCommentType, VISUAL_COMMENT_TYPE } from "../../../pages/dashboard/tabs/journal/comment-types";

const GENERIC_BINARY_CONTENT_TYPE = "application/octet-stream";

const FALLBACK_CONTENT_TYPE_BY_COMMENT_TYPE: Partial<Record<number, string>> = {
  [VISUAL_COMMENT_TYPE.photo]: "image/*",
  [VISUAL_COMMENT_TYPE.audio]: "audio/*",
  [VISUAL_COMMENT_TYPE.video]: "video/*",
  [VISUAL_COMMENT_TYPE.document]: GENERIC_BINARY_CONTENT_TYPE,
};

export const getVisualCommentFileContentType = (commentType: number, mimeType: string | null | undefined): string =>
  (mimeType && mimeType !== GENERIC_BINARY_CONTENT_TYPE
    ? mimeType
    : FALLBACK_CONTENT_TYPE_BY_COMMENT_TYPE[commentType]) || GENERIC_BINARY_CONTENT_TYPE;

export const shouldPreloadVisualCommentFile = (
  commentType: number,
  contentType: string,
  support: IMediaSupport = mediaSupport,
): boolean => {
  if (commentType === VISUAL_COMMENT_TYPE.photo) {
    return true;
  }

  if (commentType === VISUAL_COMMENT_TYPE.audio || commentType === VISUAL_COMMENT_TYPE.video) {
    if (contentType.endsWith("/*")) {
      return false;
    }

    return support.canPlay(contentType);
  }

  return false;
};

interface IVisualCommentFileSource {
  commentId: number;
  commentType: number;
  fileName?: string | null;
  mimeType?: string | null;
}

export interface IVisualCommentFileDescriptor {
  commentId: number;
  contentType: string;
  fileName?: string;
  shouldPreload: boolean;
}

export const createVisualCommentFileDescriptor = (
  comment: IVisualCommentFileSource,
  support: IMediaSupport = mediaSupport,
): IVisualCommentFileDescriptor | null => {
  if (!isVisualFileCommentType(comment.commentType)) {
    return null;
  }

  if (comment.fileName === null && comment.mimeType === null) {
    return null;
  }

  const contentType = getVisualCommentFileContentType(comment.commentType, comment.mimeType);

  return {
    commentId: comment.commentId,
    contentType,
    fileName: comment.fileName || undefined,
    shouldPreload: shouldPreloadVisualCommentFile(comment.commentType, contentType, support),
  };
};

export const mergeVisualCommentFileContentType = (currentContentType: string, responseContentType: string): string =>
  responseContentType && responseContentType !== GENERIC_BINARY_CONTENT_TYPE ? responseContentType : currentContentType;
